import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronLeft, UserRound } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';

interface ContestMobileHeaderProps {
  title: string;
  subtitle?: ReactNode;
  onBack: () => void;
}

const ContestMobileHeader = ({ title, subtitle, onBack }: ContestMobileHeaderProps) => {
  const { isAuthenticated, logout, user } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isMenuOpen) return;

    const closeOnOutsidePress = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setIsMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };

    window.addEventListener('pointerdown', closeOnOutsidePress);
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      window.removeEventListener('pointerdown', closeOnOutsidePress);
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isMenuOpen]);

  const initial = user?.username?.trim().charAt(0).toUpperCase();

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-surface-card/95 pt-[env(safe-area-inset-top)] backdrop-blur-xl lg:hidden">
      <div className="grid min-h-[4.5rem] grid-cols-[2.75rem_minmax(0,1fr)_2.75rem] items-center gap-2 px-2">
        <button
          type="button"
          onClick={onBack}
          className="flex h-11 w-11 items-center justify-center rounded-full text-accent-light-blue transition duration-150 hover:bg-white/5 active:scale-[0.96] active:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
          aria-label="Back to contests"
        >
          <ChevronLeft className="h-6 w-6" strokeWidth={2.25} aria-hidden="true" />
        </button>

        <div className="min-w-0 pr-1">
          <h1 className="truncate font-heading text-[17px] font-bold leading-tight text-white">
            {title}
          </h1>
          {subtitle ? (
            <div className="mt-1 flex min-w-0 items-center gap-1.5 truncate text-xs font-medium leading-tight text-accent-light-blue/80 tabular-nums">
              {subtitle}
            </div>
          ) : null}
        </div>

        {isAuthenticated ? (
          <div ref={menuRef} className="relative flex justify-end">
            <button
              type="button"
              onClick={() => setIsMenuOpen((open) => !open)}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/70 text-sm font-bold text-accent-light-blue ring-1 ring-inset ring-white/10 transition duration-150 hover:bg-primary active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
              aria-label="User menu"
            >
              {initial || <UserRound className="h-5 w-5" strokeWidth={2} aria-hidden="true" />}
            </button>

            {isMenuOpen ? (
              <div
                className="absolute right-0 top-[calc(100%+0.5rem)] z-[60] w-48 overflow-hidden rounded-lg border border-white/10 bg-surface-raised py-1 shadow-xl shadow-black/30"
                role="menu"
                aria-label="User menu"
              >
                <div className="truncate border-b border-white/10 px-4 py-2 text-xs text-gray-400">
                  {user?.username}
                </div>
                <Link
                  to="/profile"
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-4 py-2.5 text-sm text-gray-200 transition-colors hover:bg-primary/60 hover:text-accent-yellow active:bg-primary"
                  role="menuitem"
                >
                  My Profile
                </Link>
                {user?.is_superuser ? (
                  <Link
                    to="/admin"
                    onClick={() => setIsMenuOpen(false)}
                    className="block px-4 py-2.5 text-sm text-gray-200 transition-colors hover:bg-primary/60 hover:text-accent-yellow active:bg-primary"
                    role="menuitem"
                  >
                    Admin
                  </Link>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setIsMenuOpen(false);
                    void logout();
                  }}
                  className="block w-full px-4 py-2.5 text-left text-sm text-gray-200 transition-colors hover:bg-red-700 hover:text-white active:bg-red-800"
                  role="menuitem"
                >
                  Logout
                </button>
              </div>
            ) : null}
          </div>
        ) : (
          <Link
            to="/login"
            className="flex h-10 w-10 items-center justify-center justify-self-end rounded-full text-accent-light-blue ring-1 ring-inset ring-white/10 transition duration-150 hover:bg-white/5 active:scale-[0.96] active:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
            aria-label="Log in"
          >
            <UserRound className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
          </Link>
        )}
      </div>
    </header>
  );
};

export default ContestMobileHeader;
